import closeCompose from './actions/closeCompose';
import { ForwardMessageState } from './actions/forwardMessage';
import { messageBeingSent } from './actions/messageBeingSent';
import onCloseInlineCompose from './actions/onCloseInlineCompose';
import { TrySaveAndCloseComposeState } from './actions/trySaveAndCloseCompose';
import updateInternetMessageIdAction from './actions/updateInternetMessageId';
import ComposePlugins, { findComposePlugins } from './plugins/ComposePlugins';
import { ComposeViewStateInitProps } from './utils/createComposeViewState';
import { CreateReplyState } from './utils/createReply';
import { AttachmentFileAttributes } from 'owa-attachment-file-types';
import { LazyAction, LazyImport, LazyModule } from 'owa-bundling';
import { ClientItemId } from 'owa-client-ids';
import InfoBarMessageViewStateCreator from 'owa-info-bar/lib/schema/InfoBarMessageViewStateCreator';
import { InstrumentationContext } from 'owa-mail-store';
import 'owa-options-message-options';
import 'owa-mail-clp';
import 'owa-recipient-cache';
import EmailAddressWrapper from 'owa-service/lib/contract/EmailAddressWrapper';
import Message from 'owa-service/lib/contract/Message';
// import owa-options-message-options to load the message options not included in sessionData
// import owa-recipient-cache to kick off the cache preload

const lazyModule = new LazyModule(() =>
    import(/* webpackChunkName: "MailCompose", webpackPrefetch: true */ './lazyIndex')
);

export let lazyReplyToMessage = new LazyAction(lazyModule, m => m.replyToMessage);
export let lazyForwardMessage = new LazyAction(lazyModule, m => m.forwardMessage);
export let lazyCloseCompose = new LazyAction(lazyModule, m => m.closeCompose);
export let lazyDiscardCompose = new LazyAction(lazyModule, m => m.discardCompose);
export let lazyLoadDraftToCompose = new LazyAction(lazyModule, m => m.loadDraftToCompose);
export let lazyOpenCompose = new LazyAction(lazyModule, m => m.openCompose);
export let lazyTrySaveAndCloseCompose = new LazyAction(lazyModule, m => m.trySaveAndCloseCompose);
export let lazyMoveComposeToTab = new LazyAction(lazyModule, m => m.moveComposeToTab);
export const lazyRecreateComposeFromViewState = new LazyAction(
    lazyModule,
    m => m.recreateComposeFromViewState
);
export let lazyTryCreateResendDraft = new LazyAction(lazyModule, m => m.tryCreateResendDraft);
export let lazyReplyToApprovalMessage = new LazyAction(lazyModule, m => m.replyToApprovalMessage);
export let lazyPreloadComposePlugins = new LazyAction(lazyModule, m => m.preloadComposePlugins);
export const lazyOnEditDraftButtonClicked = new LazyAction(
    lazyModule,
    m => m.onEditDraftButtonClicked
);
export let lazyShowSubjectPickerButton = new LazyAction(lazyModule, m => m.showSubjectPickerButton);

// Delay loaded utilities
export let lazyCreateComposeViewState = new LazyImport(lazyModule, m => m.createComposeViewState);
export let lazyCreateReply = new LazyImport(lazyModule, m => m.createReply);
export let lazyGetToCcRecipientsForReply = new LazyImport(
    lazyModule,
    m => m.getToCcRecipientsForReply
);
export type FindComposePluginsType = typeof findComposePlugins;
export let lazyFindComposePlugins = new LazyImport(lazyModule, m => m.findComposePlugins);
export let lazyReplyToMessageAndCreateViewState = new LazyImport(
    lazyModule,
    m => m.replyToMessageAndCreateViewState
);
export let lazyInfoBarAddHandler = new LazyImport(lazyModule, m => m.infoBarAddHandler);
export let lazyInfoBarRemoveHandler = new LazyImport(lazyModule, m => m.infoBarRemoveHandler);
export let lazyGetNextComposeId = new LazyImport(lazyModule, m => m.getNextComposeId);
export let lazyCreateAttachments = new LazyImport(lazyModule, m => m.createAttachments);
export let lazyFindComposeFromRecipientWell = new LazyImport(
    lazyModule,
    m => m.findComposeFromRecipientWell
);

// Non-delayed loaded exports
export {
    default as getDefaultBodyType,
    HTML_BODY_TYPE,
    TEXT_BODY_TYPE,
} from './utils/getDefaultBodyType';
export { isViewStateDirty } from './utils/viewStateUtils';
export { default as updateComposeItemId } from './actions/updateComposeItemId';
export { default as getRecipientsFromWellViewState } from './utils/getRecipientsFromWellViewState';
export { default as findInlineComposeViewState } from './utils/findInlineComposeViewState';
export { default as loadAttachmentSuggestions } from './utils/loadAttachmentSuggestions';
export { default as onComposeAddinsInitialized } from './actions/onComposeAddinsInitialized';
export {
    default as onInlineComposeSendStateChange,
} from './actions/onInlineComposeSendStateChange';
export {
    default as getFullComposeShowingInTabContentArea,
} from './selectors/getFullComposeShowingInTabContentArea';

// export synchronous interfaces
export {
    ComposePlugins,
    CreateReplyState,
    AttachmentFileAttributes,
    InstrumentationContext,
    Message,
    EmailAddressWrapper,
    TrySaveAndCloseComposeState,
    ForwardMessageState,
    ComposeViewStateInitProps,
    messageBeingSent,
    ClientItemId,
    InfoBarMessageViewStateCreator,
};

export type CloseComposeAction = typeof closeCompose;
export type OnCloseInlineComposeAction = typeof onCloseInlineCompose;
export type UpdateInternetMessageIdAction = typeof updateInternetMessageIdAction;
